import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Plus, X, FilePlus2, Mail, UserPlus, Crown, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const CreateRequestModal = ({ open, onClose, clients, onCreated, onLimitReached, onRefresh }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ client_id: "", title: "", description: "", area: "", deadline: "", notify: true });
  const [tasks, setTasks] = useState([""]);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [person, setPerson] = useState({ kind: "client", name: "", email: "", access: "view" });
  const [adding, setAdding] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [newProject, setNewProject] = useState("");
  const [mode, setMode] = useState("single");
  const [bulkClients, setBulkClients] = useState([]);
  const [recipients, setRecipients] = useState("spoc");
  const [bulkProjectMode, setBulkProjectMode] = useState("general");
  const [bulkProjectName, setBulkProjectName] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [recurInterval, setRecurInterval] = useState("monthly");
  const [recurDays, setRecurDays] = useState(30);
  const [recurNotify, setRecurNotify] = useState(true);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setForm({ client_id: "", title: "", description: "", area: "", deadline: "", notify: true });
    setTasks([""]); setContacts([]); setSelected([]); setShowAdd(false);
    setPerson({ kind: "client", name: "", email: "", access: "view" });
    setProjects([]); setProjectId(""); setNewProject("");
    setMode("single"); setBulkClients([]); setRecipients("spoc"); setBulkProjectMode("general"); setBulkProjectName("");
    setRecurring(false); setRecurInterval("monthly"); setRecurDays(30); setRecurNotify(true);
  };
  const close = () => { reset(); onClose(); };

  useEffect(() => {
    const c = clients.find((x) => x.id === form.client_id);
    const cts = (c?.contacts || []).length ? c.contacts : (c?.client_contact_email ? [{ user_id: c.client_user_id, email: c.client_contact_email, is_spoc: true }] : []);
    setContacts(cts);
    const spoc = cts.find((x) => x.is_spoc) || cts[0];
    setSelected(spoc ? [spoc.user_id] : []);
    setShowAdd(false);
    setProjectId(""); setNewProject("");
    if (form.client_id) {
      api.get(`/clients/${form.client_id}/projects`).then(({ data }) => {
        setProjects(data.projects || []);
        const def = (data.projects || []).find((p) => p.is_default) || (data.projects || [])[0];
        if (def) setProjectId(def.id);
      }).catch(() => setProjects([]));
    } else { setProjects([]); }
  }, [form.client_id]); // eslint-disable-line

  const toggle = (uid) => setSelected(selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid]);

  const addPerson = async () => {
    if (!person.email.trim()) { toast({ title: "Enter an email", variant: "destructive" }); return; }
    setAdding(true);
    try {
      if (person.kind === "client") {
        const { data } = await api.post(`/clients/${form.client_id}/contacts`, { name: person.name, email: person.email, is_spoc: false });
        setContacts(data.contacts);
        const added = data.contacts.find((x) => x.email === person.email.toLowerCase());
        if (added) setSelected([...selected, added.user_id]);
        toast({ title: "Client contact added", description: `Invite sent to ${person.email}` });
      } else {
        await api.post(`/clients/${form.client_id}/members`, { name: person.name, email: person.email, access: person.access });
        toast({ title: "Firm member added", description: `${person.email} now has ${person.access === "action" ? "action" : "view"} access` });
      }
      setPerson({ kind: "client", name: "", email: "", access: "view" });
      setShowAdd(false);
      onRefresh && onRefresh();
    } catch (err) {
      toast({ title: "Could not add person", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setAdding(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const commonTasks = tasks.filter((t) => t.trim()).map((t) => ({ title: t }));
    if (mode === "multi") {
      if (bulkClients.length === 0) { toast({ title: "Select at least one client", variant: "destructive" }); return; }
      if (!form.title.trim()) { toast({ title: "Enter a title", variant: "destructive" }); return; }
      setBusy(true);
      try {
        const { data } = await api.post("/requests/bulk", {
          client_ids: bulkClients, title: form.title, description: form.description, area: form.area, deadline: form.deadline,
          tasks: commonTasks, recipients, project_mode: bulkProjectMode, project_name: bulkProjectName, notify: form.notify,
          recurring, recur_interval: recurInterval, recur_days: Number(recurDays) || 0, recur_notify: recurNotify,
        });
        const skipMsg = data.skipped?.length ? ` ${data.skipped.length} skipped (${data.skipped.map((s) => s.client).join(", ")}).` : "";
        toast({ title: `Sent to ${data.created_count} client(s)`, description: `${data.created_count} request(s) created.${skipMsg}` });
        reset(); onCreated && onCreated(); onClose();
      } catch (err) {
        toast({ title: "Bulk send failed", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
      } finally { setBusy(false); }
      return;
    }
    if (!form.client_id) { toast({ title: "Select a client", variant: "destructive" }); return; }
    if (contacts.length && selected.length === 0) { toast({ title: "Select at least one client contact", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const payload = { ...form, contact_ids: selected, tasks: commonTasks, recurring, recur_interval: recurInterval, recur_days: Number(recurDays) || 0, recur_notify: recurNotify };
      if (projectId === "__new") { payload.project_name = newProject.trim() || "New project"; payload.project_id = null; }
      else if (projectId) { payload.project_id = projectId; }
      const { data } = await api.post("/requests", payload);
      toast({ title: "Request created", description: data.request.title });
      reset(); onCreated && onCreated(); onClose();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast({ title: "Request limit reached", description: err?.response?.data?.detail || "This client has reached the Starter limit.", variant: "destructive" });
        onClose(); onLimitReached && onLimitReached();
      } else {
        toast({ title: "Could not create request", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
      }
    } finally { setBusy(false); }
  };

  const toggleBulk = (id) => setBulkClients((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="create-request-modal" className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><FilePlus2 className="h-6 w-6 text-indigo-700" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">New document request</DialogTitle>
          <DialogDescription className="text-slate-500">Describe what's needed, choose who should upload it, and break it into tasks.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-3">
          <div data-testid="mode-toggle" className="inline-flex w-full rounded-xl border border-slate-200 bg-slate-50 p-1">
            <button type="button" data-testid="mode-single" onClick={() => setMode("single")} className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-colors ${mode === "single" ? "bg-[#2f2b8f] text-white shadow-sm" : "text-slate-500"}`}>One client</button>
            <button type="button" data-testid="mode-multi" onClick={() => setMode("multi")} className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-colors ${mode === "multi" ? "bg-[#2f2b8f] text-white shadow-sm" : "text-slate-500"}`}>Multiple clients</button>
          </div>

          {mode === "multi" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Send to clients ({bulkClients.length} selected)</Label>
                  <button type="button" data-testid="bulk-select-all" onClick={() => setBulkClients(bulkClients.length === clients.length ? [] : clients.map((c) => c.id))} className="text-xs font-medium text-[#2f2b8f] hover:underline">{bulkClients.length === clients.length ? "Clear all" : "Select all"}</button>
                </div>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2">
                  {clients.map((c) => (
                    <label key={c.id} data-testid={`bulk-client-${c.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                      <Checkbox checked={bulkClients.includes(c.id)} onCheckedChange={() => toggleBulk(c.id)} />
                      <span className="text-sm text-slate-700">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Recipients</Label>
                  <Select value={recipients} onValueChange={setRecipients}>
                    <SelectTrigger data-testid="bulk-recipients" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spoc">SPOC contacts only</SelectItem>
                      <SelectItem value="all">All contacts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Project</Label>
                  <Select value={bulkProjectMode} onValueChange={setBulkProjectMode}>
                    <SelectTrigger data-testid="bulk-project-mode" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Each client's General</SelectItem>
                      <SelectItem value="named">Common named project</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {bulkProjectMode === "named" && (
                <Input data-testid="bulk-project-name" value={bulkProjectName} onChange={(e) => setBulkProjectName(e.target.value)} placeholder="Common project name, e.g. Income Tax 2026" className="rounded-xl" />
              )}
            </div>
          )}

          {mode === "single" && (
          <div className="space-y-1.5">
            <Label>Client</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="request-client-trigger" className="rounded-xl"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.nature_of_engagement}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          )}

          {mode === "single" && form.client_id && (
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="request-project-trigger" className="rounded-xl"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</SelectItem>)}
                  <SelectItem value="__new">+ New project…</SelectItem>
                </SelectContent>
              </Select>
              {projectId === "__new" && (
                <Input data-testid="request-new-project" value={newProject} onChange={(e) => setNewProject(e.target.value)} placeholder="e.g. Income Tax 2026" className="mt-1.5 rounded-xl" />
              )}
            </div>
          )}

          {mode === "single" && form.client_id && (
            <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Send to client contact(s)</Label>
                <button type="button" onClick={() => setShowAdd(!showAdd)} className="inline-flex items-center gap-1 text-xs font-medium text-[#2f2b8f] hover:underline"><UserPlus className="h-3.5 w-3.5" /> Add new person</button>
              </div>
              {contacts.length === 0 && <p className="text-xs text-slate-500">No contacts yet — add one below.</p>}
              {contacts.map((ct) => (
                <label key={ct.user_id} data-testid={`contact-option-${ct.email}`} className="flex cursor-pointer items-center gap-2 rounded-lg bg-white px-3 py-2">
                  <Checkbox checked={selected.includes(ct.user_id)} onCheckedChange={() => toggle(ct.user_id)} />
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-sm text-slate-700">{ct.email}</span>
                  {ct.is_spoc && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700"><Crown className="h-2.5 w-2.5" /> SPOC</span>}
                </label>
              ))}
              {showAdd && (
                <div className="mt-1 space-y-2 rounded-lg border border-dashed border-slate-300 bg-white p-3">
                  <Select value={person.kind} onValueChange={(v) => setPerson({ ...person, kind: v })}>
                    <SelectTrigger data-testid="person-kind-trigger" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client-side contact (can upload)</SelectItem>
                      <SelectItem value="firm">Firm-side member (view/action)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="person-name" value={person.name} onChange={(e) => setPerson({ ...person, name: e.target.value })} placeholder="Name" className="rounded-xl" />
                    <Input data-testid="person-email" type="email" value={person.email} onChange={(e) => setPerson({ ...person, email: e.target.value })} placeholder="Email" className="rounded-xl" />
                  </div>
                  {person.kind === "firm" && (
                    <Select value={person.access} onValueChange={(v) => setPerson({ ...person, access: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="view">View only</SelectItem>
                        <SelectItem value="action">Action taker</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Button data-testid="person-add-btn" type="button" onClick={addPerson} disabled={adding} className="w-full rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]">{adding ? "Adding & emailing..." : "Add & send invite"}</Button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5"><Label>Title</Label><Input data-testid="request-title" required value={form.title} onChange={set("title")} placeholder="FY24 Income Tax Filing" className="rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={set("deadline")} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Area</Label><Input data-testid="request-area" value={form.area} onChange={set("area")} placeholder="e.g. Tax, Audit, GST" className="rounded-xl" /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={set("description")} placeholder="Any details or instructions..." rows={2} className="rounded-xl" /></div>
          <div className="space-y-2">
            <Label>Tasks</Label>
            {tasks.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={t} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Task ${i + 1}`} className="rounded-xl" />
                {tasks.length > 1 && <button type="button" onClick={() => setTasks(tasks.filter((_, j) => j !== i))} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-500"><X className="h-4 w-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => setTasks([...tasks, ""])} className="inline-flex items-center gap-1 text-sm font-medium text-[#2f2b8f] hover:underline"><Plus className="h-4 w-4" /> Add task</button>
          </div>
          <div data-testid="recurring-row" className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-slate-400" />
                <div>
                  <div className="text-sm font-medium text-[#1a1a2e]">Make this recurring</div>
                  <div className="text-xs text-slate-500">Auto-recreate & re-email on a schedule until you turn it off.</div>
                </div>
              </div>
              <Switch data-testid="recurring-switch" checked={recurring} onCheckedChange={setRecurring} />
            </div>
            {recurring && (
              <div className="flex items-center gap-2">
                <Select value={recurInterval} onValueChange={setRecurInterval}>
                  <SelectTrigger data-testid="recur-interval" className="h-9 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="halfyearly">Half-yearly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="custom">Custom (every N days)</SelectItem>
                  </SelectContent>
                </Select>
                {recurInterval === "custom" && (
                  <Input data-testid="recur-days" type="number" min="1" value={recurDays} onChange={(e) => setRecurDays(e.target.value)} placeholder="days" className="h-9 w-28 rounded-xl" />
                )}
              </div>
            )}
            {recurring && (
              <div className="flex items-center justify-between border-t border-slate-200/70 pt-2">
                <div className="text-xs text-slate-500">Email client each time it auto-recreates</div>
                <Switch data-testid="recur-notify-switch" checked={recurNotify} onCheckedChange={setRecurNotify} />
              </div>
            )}
          </div>
          <div data-testid="notify-client-row" className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" />
              <div>
                <div className="text-sm font-medium text-[#1a1a2e]">Notify client by email</div>
                <div className="text-xs text-slate-500">Send an email inviting the selected contact(s) to upload documents.</div>
              </div>
            </div>
            <Switch data-testid="notify-client-switch" checked={form.notify} onCheckedChange={(v) => setForm({ ...form, notify: v })} />
          </div>
          <Button data-testid="create-request-submit" type="submit" disabled={busy} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
            {busy ? "Sending..." : mode === "multi" ? `Send to ${bulkClients.length || ""} client(s)` : "Create request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRequestModal;
